import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, useLocation, useNavigate, useSearchParams } from '@remix-run/react';
import type { AxiosError } from 'axios';
import cx from 'classnames';

import RefreshPage from './RefreshPage';

interface FallBackProps {
  isFullWidth?: boolean;
  inline?: boolean;
  errorCode?: string;
  publicationPresent?: boolean;
  customTitle?: string;
  customDescription?: ReactNode | string;
  error?: AxiosError;
}

const FallBack = ({
  isFullWidth = false,
  inline = false,
  errorCode = '404',
  publicationPresent = true,
  customTitle,
  customDescription,
  error,
}: FallBackProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  // All hooks must be called before any conditional returns
  useEffect(() => {
    if (errorCode === '404' && !searchParams.has('404')) {
      navigate(`/?${new URLSearchParams({ '404': pathname })}`);
    }
  }, [errorCode, pathname, searchParams, navigate]);

  // Handle RefreshPageError with a user-friendly message
  if (error?.message === 'RefreshPageError') {
    return <RefreshPage isFullWidth={isFullWidth} />;
  }

  const title = i18n.exists(`errors.${errorCode}.title`) ? t(`errors.${errorCode}.title`) : t('errors.fallback.title');
  const description = i18n.exists(`errors.${errorCode}.description`)
    ? t(`errors.${errorCode}.description`)
    : t('errors.fallback.description');

  return (
    <div
      className={cx(
        'mx-auto flex items-center justify-center bg-white px-4 py-24',
        inline ? '' : 'min-h-screen',
        isFullWidth ? 'w-full' : 'max-w-3xl',
      )}
    >
      <div className={cx('mx-auto', isFullWidth ? 'max-w-screen-lg' : 'max-w-max')}>
        <main className="sm:flex">
          {errorCode !== '0' && <p className="text-4xl font-extrabold text-gray-600 sm:text-5xl">{errorCode}</p>}
          <div className="sm:ml-6">
            <div className={cx('sm:pl-6', errorCode !== '0' && 'sm:border-l sm:border-gray-200')}>
              <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                {customTitle || title}
              </h1>

              <p className="mb-2 mt-2 text-base text-gray-500">{customDescription || description}</p>

              {publicationPresent && (
                <Link to="/" className="offset-1 mt-1 text-base text-gray-600 underline hover:text-gray-700">
                  {t('errors.back')}
                </Link>
              )}
              {error && (
                <details className="mt-4">
                  <summary className="text-sm font-medium text-gray-500">Error Details</summary>
                  <p className="mt-2 text-sm text-gray-900">{error.message}</p>
                  {error?.stack && (
                    <pre className="mt-2 w-[360px] overflow-x-auto text-sm text-gray-900 sm:w-[480px]">
                      {error.stack}
                    </pre>
                  )}
                </details>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FallBack;
